import React from "react";
import { Link } from 'react-router-dom';

interface AreaComparisonChartProps {
  className?: string;
}

const AreaData = [
  { 
    name: "Your Household", 
    value: "125L", 
    progress: 45, 
    color: "#0088CC"
  },
  { 
    name: "Neighborhood Average", 
    value: "180L", 
    progress: 65, 
    color: "#94A3B8"
  },
  { 
    name: "City Average", 
    value: "220L", 
    progress: 80, 
    color: "#CBD5E1"
  },
];

export const AreaComparisonChart: React.FC<AreaComparisonChartProps> = ({ className }) => {
  return (
    <section className={`w-full ${className || ""}`}>
      <div className="flex flex-col p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
        {/* Header with title and dropdown */}
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-2xl font-bold text-gray-900">Area Comparison</h3>
          <button className="flex items-center justify-between px-4 py-2 bg-white border border-gray-200 rounded-lg text-base">
            This Week
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        {/* Description text */}
        <p className="text-base text-gray-500 mb-5">
          Compare your water usage with similar homes in your area
        </p>
        
        {/* Progress bars with clear spacing */}
        <div className="flex flex-col space-y-6">
          {AreaData.map((item, index) => (
            <div key={index} className="flex flex-col mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-base font-medium text-gray-900">{item.name}</span>
                <span className="text-base font-bold text-gray-900">{item.value}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full" 
                  style={{ width: `${item.progress}%`, backgroundColor: item.color }}
                ></div>
              </div>
              
              {/* Only show info box after the first progress bar (Your Household) */}
              {index === 0 && (
                <div className="mt-3 mb-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-blue-800">Your household uses <span className="font-medium">15% less water</span> than similar homes in your area.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

interface YourBadgesProps {
  showTitle?: boolean;
}

export const YourBadges: React.FC<YourBadgesProps> = ({ showTitle = false }) => {
  return (
    <div className="mt-10 pt-4 border-t border-gray-200">
      {showTitle && <h3 className="text-xl font-bold mb-6">Your Badges</h3>}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-yellow-100 rounded-xl flex flex-col items-center justify-center py-4">
          <div className="text-orange-500 mb-2">
            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12,15c1.1,0,2-0.9,2-2s-0.9-2-2-2s-2,0.9-2,2S10.9,15,12,15z M12,9c2.2,0,4,1.8,4,4s-1.8,4-4,4s-4-1.8-4-4 S9.8,9,12,9z M12,17c2.8,0,5-2.2,5-5s-2.2-5-5-5s-5,2.2-5,5S9.2,17,12,17z M20,12c0,4.4-3.6,8-8,8s-8-3.6-8-8s3.6-8,8-8 S20,7.6,20,12z" />
            </svg>
          </div>
          <span className="text-sm font-medium text-center">First Save</span>
        </div>
        <div className="bg-blue-100 rounded-xl flex flex-col items-center justify-center py-4">
          <div className="text-blue-500 mb-2">
            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2h-2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
            </svg>
          </div>
          <span className="text-sm font-medium text-center">Water Pro</span>
        </div>
        <div className="bg-green-100 rounded-xl flex flex-col items-center justify-center py-4">
          <div className="text-green-600 mb-2">
            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 22C6.49 22 2 17.51 2 12S6.49 2 12 2s10 4.49 10 10-4.49 10-10 10zm0-18c-4.41 0-8 3.59-8 8s3.59 8 8 8 8-3.59 8-8-3.59-8-8-8zm-.3 8.5c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
            </svg>
          </div>
          <span className="text-sm font-medium text-center">Eco Hero</span>
        </div>
        <div className="bg-gray-100 rounded-xl flex flex-col items-center justify-center py-4">
          <div className="text-gray-400 mb-2">
            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
          </div>
          <span className="text-sm font-medium text-center">More</span>
        </div>
      </div>
    </div>
  );
};

// Button to view all conservation tips
export const ViewAllTipsButton: React.FC = () => {
  return (
    <div className="mt-6">
      <Link to="/conservation-tips" className="w-full block">
        <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl text-lg font-medium transition-colors">
          View All Conservation Tips
        </button>
      </Link>
    </div>
  );
};
